import { Component, Inject, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomProfileProperty } from 'app/back-office/account.model';
import { SnackBarService } from 'app/core/snack-bar.service';
import { ProfileFieldsError } from 'app/people/people';
import { PeopleService } from 'app/people/people.service';

@Component({
  selector: 'app-profile-field-editing',
  templateUrl: './profile-field-editing.component.html',
  styleUrls: ['./profile-field-editing.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, TranslateModule],
})
export class ProfileFieldEditingComponent {
  public dataProfileField: CustomProfileProperty;
  public allProfileFields: CustomProfileProperty[] = [];
  public error: ProfileFieldsError = {
    keyError: '',
    nameError: '',
  };
  public isCreate: false;
  public hintText: string;
  public cashName: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ProfileFieldEditingComponent>,
    public peopleService: PeopleService,
    private snackbarService: SnackBarService,
    private translateService: TranslateService,
  ) {
    if (data) {
      this.dataProfileField = { ...data.value };
      this.isCreate = data.isCreate;
      this.allProfileFields = data.allProfileFields;
      this.hintText = this.getActualName();
      this.cashName = this.dataProfileField.name;
    }
  }

  public validateValue(value: string, property: string): void {
    this.error[property] = '';
    if (value.trim().length > 20) {
      this.error[property] = 'generic.validation.error-password-max-20-length';
    } else if (property === 'keyError' && !/^[a-zA-Z0-9]+$/.test(value) && value.trim().length) {
      this.error[property] = 'generic.validation.error-invalid-characters';
    } else if (!value.trim().length) {
      this.error[property] = 'generic.validation.error-required';
    }
  }

  public updateValue(): void {
    if (this.cashName === this.dataProfileField.name || !this.checkDoubleProfileField(this.dataProfileField)) {
      this.closeDialog();
      return;
    }
    this.peopleService.updateProfileFields(this.makeLowerCase(this.dataProfileField)).subscribe(
      (createProfileField: CustomProfileProperty) => {
        if (createProfileField) {
          this.dialogRef.close(createProfileField);
          this.snackbarService.success('people.profileFields.message.successEdited');
        }
      },
      () => {
        this.snackbarService.error('people.profileFields.message.errorEdited');
      },
    );
  }

  public createValue(): void {
    if (!this.checkDoubleProfileField(this.dataProfileField)) {
      return;
    }
    this.peopleService.createProfileFields(this.makeLowerCase(this.dataProfileField)).subscribe(
      (updatedProfileField: CustomProfileProperty) => {
        if (updatedProfileField) {
          this.dialogRef.close(updatedProfileField);
          this.snackbarService.success('people.profileFields.message.successCreated');
        }
      },
      () => {
        this.snackbarService.error('people.profileFields.message.errorCreated');
      },
    );
  }

  private makeLowerCase(dataProfileField: CustomProfileProperty): CustomProfileProperty {
    return { ...dataProfileField, mapTo: dataProfileField.mapTo.toLowerCase() };
  }

  private checkDoubleProfileField(value: CustomProfileProperty): boolean {
    const doubleProperty = this.allProfileFields.every((item) => {
      return (
        item.name.toLowerCase().trim() !== value.name.toLowerCase().trim() &&
        (this.isCreate ? item.mapTo.toLowerCase().trim() !== value.mapTo.toLowerCase().trim() : true)
      );
    });
    if (!doubleProperty) {
      this.snackbarService.error('people.profileFields.message.errorCreatedDouble');
    }
    return doubleProperty;
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public getActualName(): string {
    return this.translateService.instant('people.profileFields.hint.javaScript', {
      key: this.isCreate ? 'sneakers' : this.dataProfileField.mapTo,
    });
  }
}
